import axios, { type AxiosError } from "axios";
import i18n from "@/i18n";

/** Origin + `/api`, same as Nest `setGlobalPrefix("api")`. Set `API_URL` in `.env`. */
export const API_ROOT = `${process.env.API_URL || ""}/api`;

const api = axios.create({
  baseURL: API_ROOT,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

let onUnauthorized: (() => void) | null = null;

export function setOnUnauthorized(cb: () => void) {
  onUnauthorized = cb;
}

api.interceptors.response.use(
  (res) => res,
  (err: AxiosError) => {
    if (err.response?.status === 401 && !err.config?.url?.includes("/auth/")) {
      onUnauthorized?.();
    }
    return Promise.reject(err);
  },
);

function messageFromStatus(status: number | undefined): string | null {
  if (status === undefined) return null;
  if (status === 403) return i18n.t("errors.forbidden");
  if (status === 404) return i18n.t("errors.notFound");
  if (status === 409) return i18n.t("errors.conflict");
  if (status === 422) return i18n.t("errors.validation");
  if (status >= 500) return i18n.t("errors.server");
  return null;
}

export function extractErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as
      { message?: string | string[] } | undefined;
    if (data?.message) {
      const raw = Array.isArray(data.message)
        ? data.message.join(", ")
        : data.message;
      return typeof raw === "string" && raw.trim()
        ? raw
        : i18n.t("errors.requestFailed");
    }
    const fromStatus = messageFromStatus(err.response?.status);
    if (fromStatus) return fromStatus;
    if (err.code === "ERR_NETWORK") {
      return i18n.t("errors.network");
    }
    if (err.message) return err.message;
  }
  if (err instanceof Error && err.message) return err.message;
  return i18n.t("errors.generic");
}

export default api;
